import type { EmitEvent } from "@/domains/core"
import type { EventEmitter } from "@/domains/library"
import type { Address } from "viem"
import type { Config } from "wagmi"
import { MinimalPoolStore } from "@/domains/minimal-pool"
import { unstakeLiquidity } from "@/domains/on-chain"

type UnstakeLiquidityActionInteraction = {
  amountWei: bigint
  chainId: number
  config: Config
  connectedAddress: Address
  emitter: EventEmitter<EmitEvent>
  minimalPool: MinimalPoolStore
  onSuccess: () => void
}

export const unstakeLiquidityActionInteraction = async ({
  amountWei,
  chainId,
  config,
  connectedAddress,
  emitter,
  minimalPool,
  onSuccess,
}: UnstakeLiquidityActionInteraction) => {
  if (!connectedAddress) {
    return emitter.emit("error-not-connected", {
      title: "Not connected",
      description: "In order to interact with this app, you first must connect your wallet.",
      variant: "error",
    })
  }

  if (!minimalPool) {
    emitter.emit("error-invalid-minimal-pool", {
      title: "Invalid minimal pool",
      description: "Unable to unstake liquidity - minimal pool information is missing",
      variant: "error",
    })
    return
  }

  if (!minimalPool.gaugeAddress) {
    emitter.emit("error-invalid-minimal-pool", {
      title: "Invalid minimal pool",
      description: "Unable to unstake liquidity - minimal pool information is missing",
      variant: "error",
    })
    return
  }

  if (minimalPool.onChainUserGaugeBalance < amountWei) {
    // --------------------------------------------------------------------------------
    // STEP ONE: Check if the user has enough balance staked in the gauge
    emitter.emit("error-insufficient-gauge-balance-staked", {
      title: "Insufficient balance",
      description: `You do not have enough staked in the ${minimalPool.displayName} gauge. You are trying to withdraw ${minimalPool.formatUnits(amountWei - minimalPool.onChainUserGaugeBalance)} more than available.`,
      variant: "error",
    })
    return
  }

  // --------------------------------------------------------------------------------
  // STEP TWO: Unstake the LP
  const unstakeLiquidityResult = await unstakeLiquidity({
    account: connectedAddress,
    chainId,
    config,
    gaugeAddress: minimalPool.gaugeAddress?.toViemAddress(),
    amount: amountWei,
  })

  if (unstakeLiquidityResult.success) {
    onSuccess?.()
    emitter.emit("success-unstake-liquidity", {
      title: `Unstake ${minimalPool.displayName} liquidity`,
      description: `Unstaked ${minimalPool.formatUnits(amountWei)} from the ${minimalPool.displayName} gauge`,
      variant: "success",
    })
    return unstakeLiquidityResult
  }

  if (unstakeLiquidityResult.success === false) {
    emitter.emit("error-unstake-liquidity", {
      title: `Unable to unstake ${minimalPool.displayName} from the gauge`,
      description: unstakeLiquidityResult.error || "Unknown error",
      variant: "error",
    })
  }

  return unstakeLiquidityResult
}
